<template>
  <div class="reports-page">
    <div class="reports-header">
      <div class="reports-titlebar">
        <div class="title">
          <h1 class="text-xl font-semibold">
            {{ $t('hit-admin.common.reports') }}
          </h1>
        </div>

        <div class="titlebar-right">
          <div v-if="reportRowCount !== null" class="records-pill">
            <span class="records-label">
              <template v-if="activeFilterCount > 0 && reportTotalAll !== null">
                {{ reportRowCount }} / {{ reportTotalAll }}
              </template>
              <template v-else>
                {{ reportRowCount }}
              </template>
            </span>
            <span class="records-suffix">enregistrements</span>
          </div>

          <div v-if="activeFilterCount > 0" class="active-filters-top">
            <span class="dot" />
            <span class="txt">
              Filtre actif sur {{ activeFilterCount }}:
              <span class="cols">{{ activeFilterColumns.join(', ') }}</span>
            </span>
          </div>
        </div>
      </div>

      <div class="controls">
        <div class="report-picker">
          <hit-select
            :key="reportPickerKey"
            class="min-w-[32rem] w-full"
            :label="$t('hit-admin.reports.selectReport')"
            :options="reportOptions"
            :value="selectedReportId"
            item-id-property="id"
            :label-fields="['label']"
            clearable
            @change="onReportChange"
          />
        </div>

        <div v-if="selectedReportId === 'company-users'" class="company-picker">
          <hit-select
            class="min-w-[32rem] w-full"
            :label="'Société'"
            :options="companyOptions"
            :value="selectedCompanyId"
            item-id-property="id"
            :label-fields="['label']"
            clearable
            @change="onCompanyChange"
          />
        </div>

        <div
          v-if="selectedReportId === 'companies-no-login'"
          class="duration-picker"
        >
          <hit-select
            class="min-w-[16rem]"
            :label="'Durée'"
            :options="noLoginWeeksOptions"
            :value="selectedNoLoginWeeks"
            item-id-property="id"
            :label-fields="['label']"
            clearable="false"
            @change="onNoLoginWeeksChange"
          />
        </div>

        <div
          v-if="selectedReportId === 'companies-no-login'"
          class="never-picker"
        >
          <hit-select
            class="min-w-[18rem]"
            :label="'Dernière connexion vide'"
            :options="noLoginNeverOptions"
            :value="noLoginNeverMode"
            item-id-property="id"
            :label-fields="['label']"
            clearable="false"
            @change="onNoLoginNeverChange"
          />
        </div>

        <div
          v-if="selectedReportId === 'module-usage-monthly'"
          class="duration-picker"
        >
          <hit-select
            class="min-w-[14rem]"
            :label="'Période'"
            :options="moduleUsagePeriodModeOptions"
            :value="moduleUsagePeriodMode"
            item-id-property="id"
            :label-fields="['label']"
            clearable="false"
            @change="onModuleUsagePeriodModeChange"
          />
        </div>

        <div
          v-if="selectedReportId === 'module-usage-monthly'"
          class="duration-picker"
        >
          <hit-select
            class="min-w-[16rem]"
            :label="'Preset'"
            :options="moduleUsagePresetOptions"
            :value="moduleUsagePeriodPreset"
            item-id-property="id"
            :label-fields="['label']"
            clearable="false"
            @change="onModuleUsagePresetChange"
          />
        </div>

        <div
          v-if="
            selectedReportId === 'module-usage-monthly' &&
            moduleUsagePeriodMode === 'month'
          "
          class="duration-picker"
        >
          <input
            v-model="moduleUsageMonth"
            type="month"
            class="hit-admin-search min-w-[14rem]"
            @change="onModuleUsageFiltersChange"
          />
        </div>

        <div
          v-if="
            selectedReportId === 'module-usage-monthly' &&
            moduleUsagePeriodMode === 'range'
          "
          class="duration-picker"
        >
          <input
            v-model="moduleUsageStartDate"
            type="date"
            class="hit-admin-search min-w-[12rem]"
            @change="onModuleUsageFiltersChange"
          />
        </div>

        <div
          v-if="
            selectedReportId === 'module-usage-monthly' &&
            moduleUsagePeriodMode === 'range'
          "
          class="duration-picker"
        >
          <input
            v-model="moduleUsageEndDate"
            type="date"
            class="hit-admin-search min-w-[12rem]"
            @change="onModuleUsageFiltersChange"
          />
        </div>

        <div
          v-if="selectedReportId === 'module-usage-monthly'"
          class="company-picker"
        >
          <hit-select
            class="min-w-[18rem] w-full"
            :label="'Société'"
            :options="moduleUsageCompanyOptions"
            :value="moduleUsageCompanyId"
            item-id-property="id"
            :label-fields="['label']"
            clearable
            @change="onModuleUsageCompanyChange"
          />
        </div>

        <div
          v-if="selectedReportId === 'module-usage-monthly'"
          class="company-picker"
        >
          <hit-select
            class="min-w-[18rem] w-full"
            :label="'Utilisateur'"
            :options="moduleUsageUserOptions"
            :value="moduleUsageUserId"
            item-id-property="id"
            :label-fields="['label']"
            clearable
            @change="onModuleUsageUserChange"
          />
        </div>

        <div
          v-if="selectedReportId === 'module-usage-monthly'"
          class="company-picker"
        >
          <hit-select
            class="min-w-[18rem] w-full"
            :label="'Module'"
            :options="moduleUsageModuleOptions"
            :value="moduleUsageModuleType"
            item-id-property="id"
            :label-fields="['label']"
            clearable
            @change="onModuleUsageModuleChange"
          />
        </div>

        <div
          class="search-area"
          v-if="
            selectedReportId === 'registered-companies' ||
            selectedReportId === 'blocked-companies' ||
            selectedReportId === 'company-users' ||
            selectedReportId === 'companies-no-login' ||
            selectedReportId === 'registered-users' ||
            selectedReportId === 'module-usage-monthly'
          "
        >
          <input
            v-model="filterText"
            type="text"
            class="hit-admin-search"
            :placeholder="$t('hit-admin.reports.searchPlaceholder')"
            @keyup.enter="reloadGrid"
          />
          <hit-button
            prefix-icon="search"
            :custom-bg-color="'color-mix(in srgb, var(--color-accent,#00997c) 96%, #0b3a33)'"
            :hide-border="true"
            :icon-color-style="{color: 'rgba(255,255,255,0.88)'}"
            color="panel"
            :tooltip="$t('hit-admin.reports.search')"
            @click="reloadGrid"
          />
          <hit-button
            prefix-icon="reset_changes"
            :custom-bg-color="'color-mix(in srgb, var(--color-accent,#00997c) 96%, #0b3a33)'"
            :hide-border="true"
            :icon-color-style="{color: 'rgba(255,255,255,0.88)'}"
            color="panel"
            :tooltip="$t('hit-admin.reports.reset')"
            @click="resetAll"
          />
          <hit-button
            prefix-icon="download"
            :custom-bg-color="'color-mix(in srgb, var(--color-accent,#00997c) 96%, #0b3a33)'"
            :hide-border="true"
            :icon-color-style="{color: 'rgba(255,255,255,0.88)'}"
            color="panel"
            :tooltip="'Export Excel'"
            :disabled="exporting"
            @click="exportExcel"
          />
        </div>
      </div>
    </div>

    <div class="reports-body">
      <registered-companies-report
        v-if="
          selectedReportId === 'registered-companies' ||
          selectedReportId === 'blocked-companies'
        "
        :key="selectedReportId"
        ref="activeReport"
        :filter-text="filterText"
        :blocked-only="selectedReportId === 'blocked-companies'"
        @filters-changed="onFiltersChanged"
        @total-changed="onTotalChanged"
      />

      <company-users-report
        v-else-if="selectedReportId === 'company-users'"
        ref="activeReport"
        :filter-text="filterText"
        :company-id="selectedCompanyId"
        @filters-changed="onFiltersChanged"
        @total-changed="onTotalChanged"
      />

      <companies-no-login-report
        v-else-if="selectedReportId === 'companies-no-login'"
        ref="activeReport"
        :filter-text="filterText"
        :weeks="selectedNoLoginWeeks"
        :include-never="includeNoLoginNever"
        @filters-changed="onFiltersChanged"
        @total-changed="onTotalChanged"
      />

      <registered-users-report
        v-else-if="selectedReportId === 'registered-users'"
        ref="activeReport"
        :filter-text="filterText"
        @filters-changed="onFiltersChanged"
        @total-changed="onTotalChanged"
      />

      <module-usage-monthly-report
        v-else-if="selectedReportId === 'module-usage-monthly'"
        ref="activeReport"
        :filter-text="filterText"
        :period-mode="moduleUsagePeriodMode"
        :month="moduleUsageMonth"
        :start-date="moduleUsageStartDate"
        :end-date="moduleUsageEndDate"
        :company-id="moduleUsageCompanyId"
        :user-id="moduleUsageUserId"
        :module-type="moduleUsageModuleType"
        @filters-changed="onFiltersChanged"
        @total-changed="onTotalChanged"
      />

      <div v-else class="empty-state">
        <div class="empty-card">
          <div class="empty-title">
            {{
              (reports || []).length
                ? 'Aucun rapport sélectionné'
                : 'Aucun rapport disponible'
            }}
          </div>
          <div class="empty-subtitle">
            <template v-if="(reports || []).length">
              Sélectionnez un rapport dans la liste ci-dessus pour afficher les
              données.
            </template>
            <template v-else>
              Aucun rapport n’a été configuré côté serveur.
            </template>
          </div>
        </div>
      </div>
    </div>

    <hit-dialog
      :visible="exportDialogVisible"
      :title="'Export Excel'"
      color="panel"
      :show-cancel-button="false"
      :show-ok-button="false"
      size="small"
      @cancel="closeExportDialog('cancel')"
      @update:visible="(v) => !v && closeExportDialog('cancel')"
    >
      <template #body>
        <div class="text-sm leading-5">
          <div class="font-semibold text-slate-900">Filtres actifs</div>
          <div v-if="exportDialogMeta?.counts" class="mt-1 text-orange-500">
            <span class="font-semibold">{{
              exportDialogMeta.counts.filtered
            }}</span>
            résultats sur
            <span class="font-medium">{{ exportDialogMeta.counts.total }}</span>
          </div>
          <div
            v-else-if="exportDialogMeta?.counts?.filtered"
            class="mt-1 text-slate-700"
          >
            <span class="font-medium">{{
              exportDialogMeta.counts.filtered
            }}</span>
            résultats
          </div>

          <div v-if="exportDialogMeta" class="mt-3 grid gap-1">
            <div v-if="exportDialogMeta.search" class="text-slate-700">
              <span class="font-medium">Recherche</span>
              :
              <span class="font-mono">{{ exportDialogMeta.search }}</span>
            </div>
            <div
              v-if="exportDialogMeta.columns && exportDialogMeta.columns.length"
              class="text-slate-700"
            >
              <span class="font-medium">Colonnes filtrées</span>
              :
              <span class="font-mono">{{
                exportDialogMeta.columns.join(', ')
              }}</span>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <hit-button
          label="Annuler"
          color="button"
          @click="closeExportDialog('cancel')"
        />
        <hit-button
          label="Tout"
          color="button"
          prefix-icon="download"
          @click="closeExportDialog('all')"
        />
        <hit-button
          label="Filtré"
          color="accent"
          prefix-icon="download"
          @click="closeExportDialog('filtered')"
        />
      </template>
    </hit-dialog>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton, HitDialog, HitSelect} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';
import RegisteredCompaniesReport from './registered-companies/RegisteredCompaniesReport.vue';
import CompanyUsersReport from './company-users/CompanyUsersReport.vue';
import CompaniesNoLoginReport from './companies-no-login/CompaniesNoLoginReport.vue';
import RegisteredUsersReport from './registered-users/RegisteredUsersReport.vue';
import ModuleUsageMonthlyReport from './module-usage-monthly/ModuleUsageMonthlyReport.vue';

export default defineComponent({
  name: 'ReportsPage',
  components: {
    HitButton,
    HitDialog,
    HitSelect,
    RegisteredCompaniesReport,
    CompanyUsersReport,
    CompaniesNoLoginReport,
    RegisteredUsersReport,
    ModuleUsageMonthlyReport,
  },
  data() {
    return {
      gridApi: null,
      gridColumnApi: null,
      reports: [],
      selectedReportId: null,
      filterText: '',
      selectedCompanyId: null,
      companyOptions: [],
      moduleUsagePeriodMode: 'month',
      moduleUsagePeriodPreset: 'month-current',
      moduleUsageMonth: new Date().toISOString().slice(0, 7),
      moduleUsageStartDate: '',
      moduleUsageEndDate: '',
      moduleUsageCompanyId: null,
      moduleUsageCompanyOptions: [],
      moduleUsageUserId: null,
      moduleUsageUserOptions: [],
      moduleUsageModuleType: null,
      moduleUsageModuleOptions: [],
      selectedNoLoginWeeks: 3,
      noLoginNeverMode: 'include',
      activeFilterCount: 0,
      activeFilterColumns: [],
      pageSize: 50,
      gridColumns: [],
      selectedRow: null,
      selectedCellField: null,
      selectedCellValue: null,
      toastMessage: '',
      toastTimer: null,
      exporting: false,
      exportDialogVisible: false,
      exportDialogMeta: null,
      exportDialogResolver: null,
      reportRowCount: null,
      reportTotalAll: null,
      loadingOverlay:
        '<div class="ag-overlay-loading-center">Chargement…</div>',
      noRowsOverlay:
        '<div class="ag-overlay-loading-center">Aucune donnée</div>',
    };
  },
  computed: {
    reportOptions() {
      return (this.reports || []).map((r) => {
        return {
          id: r.id,
          label: r.label,
          description: r.description,
        };
      });
    },
    selectedReport() {
      return (this.reports || []).find((r) => r.id === this.selectedReportId);
    },
    noLoginWeeksOptions() {
      const values = [1, 2, 3, 4, 6, 8, 12, 24];
      return values.map((w) => ({
        id: w,
        label: `${w} semaine${w > 1 ? 's' : ''}`,
      }));
    },
    moduleUsagePeriodModeOptions() {
      return [
        {id: 'month', label: 'Par mois'},
        {id: 'range', label: 'Par période'},
      ];
    },
    moduleUsagePresetOptions() {
      return [
        {id: 'month-current', label: 'Mois en cours'},
        {id: 'month-previous', label: 'Mois précédent'},
        {id: 'last-7-days', label: '7 derniers jours'},
        {id: 'last-30-days', label: '30 derniers jours'},
        {id: 'custom', label: 'Personnalisé'},
      ];
    },
    noLoginNeverOptions() {
      return [
        {id: 'include', label: 'Inclure'},
        {id: 'exclude', label: 'Exclure'},
      ];
    },
    includeNoLoginNever() {
      return this.noLoginNeverMode !== 'exclude';
    },
    reportPickerKey() {
      // Some select implementations cache labels; force refresh when the dynamic label changes.
      return `${this.selectedReportId || 'none'}-${
        this.selectedNoLoginWeeks || 3
      }`;
    },
    defaultColDef() {
      return {
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 120,
        filter: true,
        // Keep filters available via column menu, but avoid the extra (split) floating filter row
        floatingFilter: false,
        suppressHeaderMenuButton: false,
      };
    },
    columnDefs() {
      if (!this.gridColumns || this.gridColumns.length === 0) return [];
      return this.gridColumns.map((field) => ({
        field,
        headerName: field,
        tooltipField: field,
        cellClass: 'hit-modern-cell',
        valueFormatter: (p) => {
          const v = p?.value;
          if (v === null || v === undefined) return '';
          // Simple ISO date-ish formatting for readability (keeps raw data)
          if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
            return v.replace('T', ' ').replace(/(\.\d+)?Z?$/, '');
          }
          // Always return a string to avoid AG Grid trying to append non-nodes
          if (typeof v === 'object') {
            try {
              return JSON.stringify(v);
            } catch {
              return String(v);
            }
          }
          return String(v);
        },
        cellRenderer: (p) => {
          const v = p?.value;
          // Render booleans as modern pills
          if (typeof v === 'boolean') {
            const label = v ? 'TRUE' : 'FALSE';
            const cls = v
              ? 'hit-pill hit-pill-true'
              : 'hit-pill hit-pill-false';
            return `<span class="${cls}">${label}</span>`;
          }
          // AG Grid expects a string/HTMLElement from cellRenderer. Ensure we only return strings.
          const formatted = p?.valueFormatted;
          if (formatted === null || formatted === undefined) {
            if (v === null || v === undefined) return '';
            if (typeof v === 'object') {
              try {
                return JSON.stringify(v);
              } catch {
                return String(v);
              }
            }
            return String(v);
          }
          return typeof formatted === 'string' ? formatted : String(formatted);
        },
      }));
    },
  },
  async mounted() {
    await this.loadReports();
  },
  watch: {
    selectedReportId() {
      // When switching reports, reset the search
      this.filterText = '';
      this.activeFilterCount = 0;
      this.activeFilterColumns = [];
      this.reportRowCount = null;
      this.reportTotalAll = null;
      if (this.selectedReportId === 'company-users') {
        this.selectedCompanyId = null;
        this.loadCompanyOptions();
      }
      if (this.selectedReportId === 'companies-no-login') {
        this.selectedNoLoginWeeks = 3;
        this.noLoginNeverMode = 'include';
      }
      if (this.selectedReportId === 'module-usage-monthly') {
        this.resetModuleUsageFilters();
        this.loadModuleUsageFilterOptions();
      }
      this.fetchTotalAllRows();
    },
  },
  methods: {
    async loadReports() {
      const reports = await adminApi.listReports();
      this.reports = reports || [];
      if (!this.selectedReportId && this.reports.length > 0) {
        this.selectedReportId = this.reports[0].id;
      }
      // Preload columns (DB labels) for selected report if available
      const report = (this.reports || []).find(
        (r) => r.id === this.selectedReportId,
      );
      if (report?.columns?.length) {
        this.setGridColumns(report.columns);
      }
      // If grid is already ready, ensure we load immediately
      if (this.gridApi) this.reloadGrid();
    },
    onReportChange(value) {
      this.selectedReportId = value;
    },
    onCompanyChange(value) {
      this.selectedCompanyId = value;
      this.reportRowCount = null;
      this.reportTotalAll = null;
      this.fetchTotalAllRows();
      this.reloadGrid();
    },
    onNoLoginWeeksChange(value) {
      const v = Number(value);
      this.selectedNoLoginWeeks = Number.isFinite(v) && v > 0 ? v : 3;
      this.reportRowCount = null;
      this.reportTotalAll = null;
      this.fetchTotalAllRows();
      this.reloadGrid();
    },
    onNoLoginNeverChange(value) {
      this.noLoginNeverMode = value === 'exclude' ? 'exclude' : 'include';
      this.reportRowCount = null;
      this.reportTotalAll = null;
      this.fetchTotalAllRows();
      this.reloadGrid();
    },
    resetModuleUsageFilters() {
      this.moduleUsagePeriodMode = 'month';
      this.moduleUsagePeriodPreset = 'month-current';
      this.moduleUsageMonth = new Date().toISOString().slice(0, 7);
      this.moduleUsageStartDate = '';
      this.moduleUsageEndDate = '';
      this.moduleUsageCompanyId = null;
      this.moduleUsageUserId = null;
      this.moduleUsageModuleType = null;
    },
    moduleUsageQueryParams() {
      return {
        month:
          this.moduleUsagePeriodMode === 'month' ? this.moduleUsageMonth : null,
        startDate:
          this.moduleUsagePeriodMode === 'range'
            ? this.moduleUsageStartDate || null
            : null,
        endDate:
          this.moduleUsagePeriodMode === 'range'
            ? this.moduleUsageEndDate || null
            : null,
        companyId: this.moduleUsageCompanyId || null,
        userId: this.moduleUsageUserId || null,
        moduleType: this.moduleUsageModuleType || null,
      };
    },
    async loadModuleUsageFilterOptions() {
      try {
        const companyRes = await adminApi.listCompanyOptions({limit: 500});
        this.moduleUsageCompanyOptions = (companyRes?.rows || []).map((r) => {
          const label = [r.designation, r.vat_no].filter(Boolean).join(' – ');
          return {id: r.id, label: label || r.id};
        });
      } catch {
        this.moduleUsageCompanyOptions = [];
      }

      try {
        const usersRes = await adminApi.listModuleUsageUsers({
          ...this.moduleUsageQueryParams(),
          limit: 500,
        });
        this.moduleUsageUserOptions = usersRes?.rows || [];
      } catch {
        this.moduleUsageUserOptions = [];
      }

      try {
        const modulesRes = await adminApi.listModuleUsageModules({
          ...this.moduleUsageQueryParams(),
          limit: 500,
        });
        this.moduleUsageModuleOptions = modulesRes?.rows || [];
      } catch {
        this.moduleUsageModuleOptions = [];
      }
    },
    async onModuleUsageFiltersChange() {
      this.reportRowCount = null;
      this.reportTotalAll = null;
      await this.loadModuleUsageFilterOptions();
      this.fetchTotalAllRows();
      this.reloadGrid();
    },
    onModuleUsagePeriodModeChange(value) {
      this.moduleUsagePeriodMode = value === 'range' ? 'range' : 'month';
      this.moduleUsagePeriodPreset = 'custom';
      this.onModuleUsageFiltersChange();
    },
    onModuleUsagePresetChange(value) {
      const preset = value || 'month-current';
      this.moduleUsagePeriodPreset = preset;
      const now = new Date();
      const toYmd = (d) => d.toISOString().slice(0, 10);
      const toYm = (d) => d.toISOString().slice(0, 7);
      if (preset === 'month-current') {
        this.moduleUsagePeriodMode = 'month';
        this.moduleUsageMonth = toYm(now);
      } else if (preset === 'month-previous') {
        this.moduleUsagePeriodMode = 'month';
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        this.moduleUsageMonth = toYm(prev);
      } else if (preset === 'last-7-days' || preset === 'last-30-days') {
        this.moduleUsagePeriodMode = 'range';
        const days = preset === 'last-7-days' ? 7 : 30;
        const end = new Date(now);
        const start = new Date(now);
        start.setDate(start.getDate() - (days - 1));
        this.moduleUsageStartDate = toYmd(start);
        this.moduleUsageEndDate = toYmd(end);
      }
      this.onModuleUsageFiltersChange();
    },
    onModuleUsageCompanyChange(value) {
      this.moduleUsageCompanyId = value || null;
      this.moduleUsageUserId = null;
      this.moduleUsageModuleType = null;
      this.onModuleUsageFiltersChange();
    },
    onModuleUsageUserChange(value) {
      this.moduleUsageUserId = value || null;
      this.moduleUsageModuleType = null;
      this.onModuleUsageFiltersChange();
    },
    onModuleUsageModuleChange(value) {
      this.moduleUsageModuleType = value || null;
      this.onModuleUsageFiltersChange();
    },
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listCompanyOptions({limit: 400});
        const rows = res?.rows || [];
        this.companyOptions = rows.map((r) => {
          const label = [r.designation, r.vat_no].filter(Boolean).join(' – ');
          return {id: r.id, label: label || r.id};
        });

        if (!this.selectedCompanyId && this.companyOptions.length > 0) {
          this.selectedCompanyId = this.companyOptions[0].id;
          this.reportRowCount = null;
          this.reportTotalAll = null;
          this.fetchTotalAllRows();
          this.reloadGrid();
        }
      } catch {
        this.companyOptions = [];
      }
    },
    refreshOnly() {
      this.$refs.activeReport?.refreshOnly?.();
    },
    resetAll() {
      this.filterText = '';
      if (this.selectedReportId === 'module-usage-monthly') {
        this.resetModuleUsageFilters();
      }
      this.$refs.activeReport?.resetAll?.();
      if (this.selectedReportId === 'module-usage-monthly') {
        this.loadModuleUsageFilterOptions();
      }
    },
    reloadGrid() {
      this.$refs.activeReport?.reloadGrid?.();
    },
    askExportScope(meta) {
      // Returns: 'filtered' | 'all' | 'cancel'
      this.exportDialogMeta = meta || null;
      this.exportDialogVisible = true;
      return new Promise((resolve) => {
        this.exportDialogResolver = resolve;
      });
    },
    closeExportDialog(choice) {
      this.exportDialogVisible = false;
      const resolve = this.exportDialogResolver;
      this.exportDialogResolver = null;
      this.exportDialogMeta = null;
      if (typeof resolve === 'function') resolve(choice || 'cancel');
    },
    async exportExcel() {
      if (!this.selectedReportId) return;
      if (this.exporting) return;

      const report = this.$refs.activeReport;
      const state = report?.getExportState?.()
        ? report.getExportState()
        : {
            filterText: this.filterText || '',
            columnFiltersJson: null,
            sortBy: null,
            sortDir: null,
            hasActiveFilters:
              (this.filterText || '').trim().length > 0 ||
              (this.activeFilterCount || 0) > 0,
          };

      let includeFilters = false;
      if (state?.hasActiveFilters) {
        const totalAll = await this.fetchTotalAllRows();

        const filtered = state?.filteredTotal ?? null;
        const counts =
          typeof filtered === 'number' && typeof totalAll === 'number'
            ? {filtered, total: totalAll}
            : typeof filtered === 'number'
              ? {filtered}
              : null;

        const choice = await this.askExportScope({
          search: (state?.filterText || '').trim() || null,
          columns: state?.activeColumns || [],
          counts,
        });
        if (choice === 'cancel') return;
        includeFilters = choice === 'filtered';
      }

      this.exporting = true;
      try {
        const params = {
          sortBy: state?.sortBy || null,
          sortDir: state?.sortDir || null,
          filterText: includeFilters ? state?.filterText || null : null,
          columnFilters: includeFilters
            ? state?.columnFiltersJson || null
            : null,
          companyId:
            this.selectedReportId === 'company-users'
              ? this.selectedCompanyId || null
              : this.selectedReportId === 'module-usage-monthly'
                ? state?.companyId || this.moduleUsageCompanyId || null
                : null,
          weeks:
            this.selectedReportId === 'companies-no-login'
              ? state?.weeks || this.selectedNoLoginWeeks || 3
              : null,
          includeNever:
            this.selectedReportId === 'companies-no-login'
              ? state?.includeNever !== false
              : null,
          month:
            this.selectedReportId === 'module-usage-monthly'
              ? state?.month ||
                (this.moduleUsagePeriodMode === 'month'
                  ? this.moduleUsageMonth
                  : null)
              : null,
          startDate:
            this.selectedReportId === 'module-usage-monthly'
              ? state?.startDate ||
                (this.moduleUsagePeriodMode === 'range'
                  ? this.moduleUsageStartDate || null
                  : null)
              : null,
          endDate:
            this.selectedReportId === 'module-usage-monthly'
              ? state?.endDate ||
                (this.moduleUsagePeriodMode === 'range'
                  ? this.moduleUsageEndDate || null
                  : null)
              : null,
          userId:
            this.selectedReportId === 'module-usage-monthly'
              ? state?.userId || this.moduleUsageUserId || null
              : null,
          moduleType:
            this.selectedReportId === 'module-usage-monthly'
              ? state?.moduleType || this.moduleUsageModuleType || null
              : null,
        };

        const exportEndpoint =
          this.selectedReport?.exportEndpoint ||
          '/reports/registered-companies/export';
        const blob = await adminApi.exportReportXlsx(exportEndpoint, params);
        const url = URL.createObjectURL(blob);
        try {
          const a = document.createElement('a');
          const ts = new Date()
            .toISOString()
            .slice(0, 16)
            .replace(/[-:]/g, '')
            .replace('T', '-');
          a.href = url;
          a.download = `${this.selectedReportId || 'report'}-${ts}.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        } finally {
          URL.revokeObjectURL(url);
        }
      } finally {
        this.exporting = false;
      }
    },
    onFiltersChanged(payload) {
      this.activeFilterCount = payload?.count || 0;
      this.activeFilterColumns = payload?.columns || [];
    },
    onTotalChanged(payload) {
      const t = payload?.total;
      this.reportRowCount = typeof t === 'number' ? t : null;
    },
    async fetchTotalAllRows() {
      try {
        if (this.selectedReportId === 'company-users') {
          if (!this.selectedCompanyId) {
            this.reportTotalAll = null;
            return null;
          }
          const res = await adminApi.getCompanyUsers({
            page: 1,
            pageSize: 1,
            sortBy: null,
            sortDir: null,
            filterText: null,
            columnFilters: null,
            companyId: this.selectedCompanyId,
          });
          const total = res?.total;
          this.reportTotalAll = typeof total === 'number' ? total : null;
          return this.reportTotalAll;
        }

        if (this.selectedReportId === 'companies-no-login') {
          const res = await adminApi.getCompaniesNoLogin({
            page: 1,
            pageSize: 1,
            sortBy: null,
            sortDir: null,
            filterText: null,
            columnFilters: null,
            weeks: this.selectedNoLoginWeeks || 3,
            includeNever: this.includeNoLoginNever,
          });
          const total = res?.total;
          this.reportTotalAll = typeof total === 'number' ? total : null;
          return this.reportTotalAll;
        }

        if (this.selectedReportId === 'registered-users') {
          const res = await adminApi.getRegisteredUsers({
            page: 1,
            pageSize: 1,
            sortBy: null,
            sortDir: null,
            filterText: null,
            columnFilters: null,
          });
          const total = res?.total;
          this.reportTotalAll = typeof total === 'number' ? total : null;
          return this.reportTotalAll;
        }

        if (this.selectedReportId === 'module-usage-monthly') {
          const res = await adminApi.getModuleUsageMonthly({
            page: 1,
            pageSize: 1,
            sortBy: null,
            sortDir: null,
            filterText: null,
            columnFilters: null,
            ...this.moduleUsageQueryParams(),
          });
          const total = res?.total;
          this.reportTotalAll = typeof total === 'number' ? total : null;
          return this.reportTotalAll;
        }

        const res = await adminApi.getRegisteredCompanies({
          page: 1,
          pageSize: 1,
          sortBy: null,
          sortDir: null,
          filterText: null,
          columnFilters: null,
          blockedOnly: this.selectedReportId === 'blocked-companies',
        });
        const total = res?.total;
        this.reportTotalAll = typeof total === 'number' ? total : null;
        return this.reportTotalAll;
      } catch {
        this.reportTotalAll = null;
        return null;
      }
    },
    setGridColumns(columns) {
      this.gridColumns = Array.isArray(columns) ? columns : [];
      if (!this.gridApi) return;
      const defs = this.columnDefs;
      if (typeof this.gridApi.setGridOption === 'function') {
        this.gridApi.setGridOption('columnDefs', defs);
      } else if (typeof this.gridApi.setColumnDefs === 'function') {
        this.gridApi.setColumnDefs(defs);
      }
    },
    buildDatasource() {
      const self = this;
      return {
        async getRows(params) {
          try {
            if (self.selectedReportId !== 'registered-companies') {
              params.successCallback([], 0);
              return;
            }

            const startRow = params.startRow ?? 0;
            const endRow = params.endRow ?? self.pageSize;
            const pageSize = endRow - startRow;
            const page = Math.floor(startRow / pageSize) + 1;

            const sortModel = params.sortModel || [];
            const primarySort = sortModel[0] || null;
            const sortBy = primarySort?.colId || null;
            const sortDir = primarySort?.sort || null;

            const res = await adminApi.getRegisteredCompanies({
              page,
              pageSize,
              sortBy,
              sortDir,
              filterText: self.filterText || null,
            });

            if (res?.columns?.length) {
              self.setGridColumns(res.columns);
            } else if (self.gridColumns.length === 0 && res?.rows?.length) {
              self.setGridColumns(Object.keys(res.rows[0]));
            }

            const rows = (res?.rows || []).map((r) => ({...r}));
            params.successCallback(rows, res?.total ?? rows.length);
          } catch (e) {
            // Fail gracefully: show empty set
            params.failCallback();
          }
        },
      };
    },
    onCellClicked(event) {
      // event.data is the full row object
      if (event && event.data) {
        this.selectedRow = event.data;
        this.selectedCellField =
          event.colDef?.field || event.column?.getColId?.() || null;
        this.selectedCellValue =
          this.selectedCellField &&
          Object.prototype.hasOwnProperty.call(
            event.data,
            this.selectedCellField,
          )
            ? event.data[this.selectedCellField]
            : event.value;
      }
    },
    formatCellValue(value) {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number') return String(value);
      if (typeof value === 'object') {
        try {
          // Compact JSON for panel readability
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      return String(value);
    },
    formatCellValuePretty(value) {
      if (value === null || value === undefined) return '';
      if (typeof value !== 'object') return this.formatCellValue(value);
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return this.formatCellValue(value);
      }
    },
    async copySelectedValue() {
      try {
        const text =
          this.selectedCellValue && typeof this.selectedCellValue === 'object'
            ? this.formatCellValuePretty(this.selectedCellValue)
            : this.formatCellValue(this.selectedCellValue);
        await navigator.clipboard.writeText(text || '');
        this.showToast('Copié avec succès');
      } catch {
        this.showToast('Copie impossible');
      }
    },
    showToast(message) {
      this.toastMessage = message || '';
      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
        this.toastTimer = null;
      }
      this.toastTimer = setTimeout(() => {
        this.toastMessage = '';
        this.toastTimer = null;
      }, 1800);
    },
    setupHorizontalScrollbarWheel() {
      try {
        if (!this.gridApi || typeof this.gridApi.getGui !== 'function') return;
        const gui = this.gridApi.getGui();
        if (!gui) return;
        const hScroll = gui.querySelector?.('.ag-body-horizontal-scroll');
        const hViewport = gui.querySelector?.(
          '.ag-body-horizontal-scroll-viewport',
        );
        const target = hViewport || hScroll;
        if (!target) return;

        // Translate vertical wheel into horizontal scroll when hovering scrollbar area
        target.addEventListener(
          'wheel',
          (e) => {
            // Only help with vertical wheel deltas
            const dy = e?.deltaY ?? 0;
            if (!dy) return;
            // If user already scrolls horizontally, let default behavior happen
            const dx = e?.deltaX ?? 0;
            if (dx) return;
            // Prevent page scroll while interacting with scrollbar
            e.preventDefault?.();
            const viewport =
              hViewport ||
              gui.querySelector?.('.ag-body-horizontal-scroll-viewport') ||
              target;
            if (viewport) viewport.scrollLeft += dy;
          },
          {passive: false},
        );
      } catch {
        // ignore
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.reports-page {
  /* Soft slate borders (avoid "black" look on white) */
  --ho-border: rgba(15, 23, 42, 0.08);
  --ho-border-strong: rgba(15, 23, 42, 0.12);
  --ho-divider: rgba(15, 23, 42, 0.1);

  /* Slight tint so the UI doesn't feel "full white" */
  background: linear-gradient(
    180deg,
    rgba(2, 6, 23, 0.02) 0%,
    rgba(2, 6, 23, 0.01) 45%,
    rgba(255, 255, 255, 1) 100%
  );
  border-radius: 0.85rem;
  padding: 0.75rem;
  height: 100%;
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr);
  gap: 0.75rem;
}

.reports-header {
  display: grid;
  gap: 0.5rem;
  padding: 0.5rem 0.65rem 0.65rem;
  border-bottom: 1px solid var(--ho-border);
  /* Slight frosted header band to break up the white */
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(6px);
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
}

.reports-titlebar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.titlebar-right {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-left: auto;
}

.records-pill {
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  border: 1px solid var(--ho-border);
  background: rgba(255, 255, 255, 0.75);
  color: rgba(15, 23, 42, 0.78);
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.04);
  white-space: nowrap;
}

.records-label {
  font-weight: 800;
  color: rgba(15, 23, 42, 0.9);
}

.records-suffix {
  font-size: 0.78rem;
  color: rgba(100, 116, 139, 1);
}

.subtitle {
  font-size: 0.875rem;
  color: rgba(100, 116, 139, 1);
  margin: 0.1rem 0 0;
}

.actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Make icon-only buttons feel more premium */
:deep(.reports-page .hit-button) {
  border-radius: 0.7rem;
}

:deep(.reports-page .hit-button:not(:disabled)) {
  transform: translateZ(0);
  transition:
    box-shadow 120ms ease,
    transform 120ms ease,
    filter 120ms ease;
}

:deep(.reports-page .hit-button:not(:disabled):hover) {
  box-shadow:
    0 1px 1px rgba(16, 24, 40, 0.06),
    0 10px 26px rgba(16, 24, 40, 0.12);
  transform: translateY(-0.5px);
}

:deep(.reports-page .hit-button:not(:disabled):active) {
  transform: translateY(0);
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.06);
}

:deep(.reports-page .hit-button:focus-visible) {
  outline: none;
  box-shadow:
    0 0 0 3px color-mix(in srgb, var(--color-accent, #00997c) 30%, transparent),
    0 8px 20px rgba(16, 24, 40, 0.1);
}

.controls {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.85rem;
}

.company-picker {
  align-self: flex-end;
}

.duration-picker {
  align-self: flex-end;
}

.never-picker {
  align-self: flex-end;
}

.search-area {
  display: flex;
  align-items: end;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-left: auto;
}

.active-filters-top {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0 0.25rem;
  color: rgba(15, 23, 42, 0.72);
  font-size: 0.78rem;
  white-space: nowrap;
}

.active-filters-top .dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(249, 115, 22, 0.95);
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.12);
}

.active-filters-top .cols {
  color: rgba(15, 23, 42, 0.82);
  font-weight: 600;
}

.reports-content {
  min-height: 0;
  height: 100%;
  display: grid;
  gap: 1rem;
}

.reports-content.detailsOpen {
  grid-template-columns: minmax(0, 1fr) 22rem;
}

.reports-grid-wrapper {
  min-height: 0;
  height: 100%;
  position: relative;
}

.reports-content.detailsOpen .reports-grid-wrapper::after {
  content: '';
  position: absolute;
  top: 0;
  right: -0.5rem;
  width: 1px;
  height: 100%;
  background: var(--ho-divider);
  pointer-events: none;
}

.details-panel {
  min-height: 0;
  height: 100%;
  border-radius: 0.85rem;
  border: 1px solid var(--ho-border);
  background: rgba(248, 250, 252, 1);
  box-shadow:
    0 1px 1px rgba(16, 24, 40, 0.04),
    0 12px 30px rgba(16, 24, 40, 0.1);
  overflow: hidden;
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr);
}

.details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  background: var(--color-panel, #f3f4f6);
  border-bottom: 1px solid var(--ho-border);
  border-left: 4px solid var(--color-accent, #00997c);
}

.details-title {
  font-weight: 650;
  color: rgba(15, 23, 42, 1);
}

.details-body {
  padding: 0.75rem;
  overflow: auto;
  display: grid;
  gap: 0.75rem;
  align-content: start;
}

.focus-card {
  border-radius: 0.85rem;
  border: 1px solid var(--ho-border);
  background: #ffffff;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.04);
  overflow: hidden;
}

.focus-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  background: linear-gradient(
    180deg,
    rgba(2, 6, 23, 0.02) 0%,
    rgba(255, 255, 255, 1) 100%
  );
  border-bottom: 1px solid var(--ho-border);
}

.focus-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(100, 116, 139, 1);
}

.focus-value {
  padding: 0.75rem;
  font-size: 0.9rem;
  color: rgba(15, 23, 42, 1);
  word-break: break-word;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
}

.pre {
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  line-height: 1.2rem;
  background: rgba(248, 250, 252, 1);
  border: 1px solid var(--ho-border);
  border-radius: 0.65rem;
  padding: 0.5rem 0.6rem;
  max-height: 18rem;
  overflow: auto;
  white-space: pre-wrap;
}

/* (removed) kv/k/v styles: no longer used after removing the row id block */

.reports-grid {
  width: 100%;
  height: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid var(--ho-border);
  box-shadow:
    0 1px 1px rgba(16, 24, 40, 0.04),
    0 8px 24px rgba(16, 24, 40, 0.06);
}

.reports-grid.ag-theme-alpine {
  /* Stay consistent with Hit-Online theme (Tailwind colors are backed by these CSS vars) */
  --ag-foreground-color: rgba(15, 23, 42, 0.82);
  --ag-background-color: #ffffff;
  /* Header: stronger Hit green + white text */
  --ag-header-foreground-color: rgba(255, 255, 255, 0.96);
  --ag-header-background-color: color-mix(
    in srgb,
    var(--color-accent, #00997c) 96%,
    #0b3a33
  );

  /* Zebra + hover/selected using Hit accent */
  --ag-odd-row-background-color: rgba(2, 6, 23, 0.02);
  --ag-row-hover-color: color-mix(
    in srgb,
    var(--color-accent, #00997c) 8%,
    #ffffff
  );
  --ag-selected-row-background-color: color-mix(
    in srgb,
    var(--color-accent, #00997c) 14%,
    #ffffff
  );

  --ag-border-color: var(--ho-border);
  --ag-input-border-color: var(--ho-border-strong);
  --ag-accent-color: var(--color-accent, #00997c);
  --ag-range-selection-border-color: var(--color-accent, #00997c);
  --ag-alpine-active-color: var(--color-accent, #00997c);
  --ag-font-size: 13px;
  --ag-font-family: var(--font-family, ui-sans-serif, system-ui, -apple-system);
  --ag-row-border-color: rgba(2, 6, 23, 0.05);
}

/* Header labels */
:deep(.reports-grid .ag-header-cell-label) {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.01em;
  font-size: 0.75rem;
  justify-content: flex-start;
}

:deep(.reports-grid .ag-header) {
  border-bottom: 1px solid var(--ho-border);
  /* AG Grid adds a darker separator shadow by default; remove it */
  box-shadow: none !important;
}

/* Ensure no extra thick line between header and first rows */
::deep(.reports-grid .ag-header-row) {
  border-bottom: none !important;
}

:deep(.reports-grid .ag-header-cell) {
  background: var(--ag-header-background-color);
  padding-left: 12px;
  padding-right: 12px;
}

:deep(.reports-grid .ag-header-cell::after) {
  /* subtle separators */
  opacity: 0.45;
}

:deep(.reports-grid .ag-header-cell-text) {
  color: rgba(255, 255, 255, 0.96);
}

/* Make header icons match the green header text */
::deep(.reports-grid .ag-header-cell .ag-icon) {
  color: rgba(255, 255, 255, 0.82);
  opacity: 0.95;
}

/* Some AG Grid themes render icons via svg fill */
::deep(.reports-grid .ag-header-cell .ag-icon svg) {
  fill: rgba(255, 255, 255, 0.82);
}

/* Make filter/sort icons a bit more subtle */
::deep(.reports-grid .ag-header-icon) {
  opacity: 0.78;
}

/* Hover affordance: apply directly on icon buttons (reliable in AG Grid) */
::deep(.reports-grid .ag-header-cell-menu-button),
::deep(.reports-grid .ag-header-icon),
::deep(.reports-grid .ag-filter-icon) {
  border-radius: 8px;
  transition:
    background-color 120ms ease,
    border-color 120ms ease;
}

::deep(.reports-grid .ag-header-cell-menu-button:hover),
::deep(.reports-grid .ag-header-icon:hover),
::deep(.reports-grid .ag-filter-icon:hover) {
  background-color: rgba(255, 255, 255, 0.22);
}

::deep(.reports-grid .ag-header-cell-menu-button:active),
::deep(.reports-grid .ag-header-icon:active),
::deep(.reports-grid .ag-filter-icon:active) {
  background-color: rgba(255, 255, 255, 0.3);
}

:deep(.reports-grid .ag-root-wrapper) {
  background: #ffffff;
}

:deep(.reports-grid .ag-body) {
  border-top: none;
}

/* Remove the tiny visual gap between header and first row */
:deep(.reports-grid .ag-header + .ag-body) {
  margin-top: 0 !important;
}
:deep(.reports-grid .ag-body-viewport) {
  padding-top: 0 !important;
}

:deep(.reports-grid .ag-row) {
  border-bottom: 1px solid var(--ag-row-border-color);
}

/* Slightly stronger bottom border on odd rows for a crisp rhythm */
:deep(.reports-grid .ag-row-odd) {
  border-bottom-color: var(--ho-border);
}

.hit-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 1.1rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.7rem;
  letter-spacing: 0.02em;
  border: 1px solid var(--ho-border);
}
.hit-pill-true {
  background: color-mix(in srgb, var(--color-accent, #00997c) 14%, #ffffff);
  color: #03695a;
  border-color: color-mix(in srgb, var(--color-accent, #00997c) 30%, #ffffff);
}
.hit-pill-false {
  background: rgba(220, 38, 38, 0.08);
  color: #991b1b;
  border-color: rgba(220, 38, 38, 0.18);
}

/* Make cells feel less cramped */
:deep(.reports-grid .ag-cell) {
  padding-left: 12px !important;
  padding-right: 12px !important;
  line-height: 1.35rem;
  display: flex;
  align-items: center;
  color: rgba(15, 23, 42, 0.82);
}

/* Subtle text rhythm on zebra rows (not the same as header) */
::deep(.reports-grid .ag-row-odd .ag-cell) {
  color: rgba(15, 23, 42, 0.72);
}

/* Ensure inner value is vertically centered as well */
:deep(.reports-grid .ag-cell-wrapper) {
  align-items: center;
}

/* Modern input look for floating filters */
:deep(.reports-grid .ag-floating-filter-input) input {
  border-radius: 0.5rem;
}

.hit-admin-search {
  height: 2.5rem;
  padding: 0 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--ho-border-strong);
  background: #ffffff;
  color: rgba(15, 23, 42, 0.88);
  min-width: 18rem;
  transition:
    box-shadow 120ms ease,
    border-color 120ms ease,
    background-color 120ms ease,
    transform 120ms ease;
}

.hit-admin-search::placeholder {
  color: rgba(100, 116, 139, 0.75);
}

.hit-admin-search:hover {
  border-color: color-mix(
    in srgb,
    var(--color-accent, #00997c) 18%,
    var(--ho-border-strong)
  );
}

.hit-admin-search:focus,
.hit-admin-search:focus-visible {
  outline: none;
  border-color: color-mix(
    in srgb,
    var(--color-accent, #00997c) 35%,
    var(--ho-border-strong)
  );
  box-shadow: 0 0 0 3px
    color-mix(in srgb, var(--color-accent, #00997c) 22%, transparent);
}

.empty-state {
  height: 100%;
  min-height: 0;
  display: grid;
  place-items: start center;
  padding: 1.25rem 0.75rem 1.5rem;
  padding-top: 2rem;
}

.empty-card {
  width: min(720px, 100%);
  border-radius: 1rem;
  border: 1px solid var(--ho-border);
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(6px);
  box-shadow:
    0 1px 1px rgba(16, 24, 40, 0.04),
    0 12px 30px rgba(16, 24, 40, 0.08);
  padding: 1.25rem 1.25rem;
}

.empty-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: rgba(15, 23, 42, 0.95);
}

.empty-subtitle {
  margin-top: 0.35rem;
  color: rgba(100, 116, 139, 1);
  font-size: 0.9rem;
  line-height: 1.35rem;
}

.hit-toast {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 50;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.92);
  color: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.18),
    0 2px 6px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
  max-width: min(360px, calc(100vw - 32px));
}

.hit-toast-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-accent, #00997c) 85%, #ffffff);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.08);
  flex: 0 0 auto;
}

.hit-toast-text {
  font-size: 0.875rem;
  line-height: 1.1rem;
}

@media (max-width: 1024px) {
  .controls {
    flex-direction: column;
    align-items: stretch;
  }
  .search-area {
    justify-content: flex-start;
    margin-left: 0;
  }
  .hit-admin-search {
    min-width: 14rem;
    width: 100%;
  }
}

@media (max-width: 1200px) {
  .reports-content.detailsOpen {
    grid-template-columns: minmax(0, 1fr) 18rem;
  }
}
</style>
